import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  catchError,
  from,
  of,
  skip,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';

export class Subscribable<A> {
  private requested = false;
  private sub$ = new BehaviorSubject<A | undefined>(undefined);
  private fetchSub$ = new Subject<void>();
  private takeUntilSub$: Subject<void>;
  private destroyRef: DestroyRef;
  private pending: Promise<A | undefined> = Promise.resolve(undefined);

  constructor(fetch: () => Promise<A>) {
    this.fetchSub$
      .pipe(
        switchMap(() =>
          from(fetch()).pipe(
            catchError((err) => {
              this.requested = false; // allow retry on next load()
              console.error('Subscribable fetch failed', err);
              return of(undefined);
            })
          )
        )
      )
      .subscribe((a) => this.sub$.next(a));
  }

  load(force = false): Promise<A | undefined> {
    if (force || !this.requested) {
      this.requested = true;
      this.pending = new Promise<A | undefined>((resolve) => {
        this.sub$.pipe(skip(1), take(1)).subscribe(resolve);
      });
      this.fetchSub$.next();
    }
    return this.pending;
  }

  takeUntil(unsub$) {
    this.takeUntilSub$ = unsub$;
    this.fetchSub$.pipe(takeUntil(this.takeUntilSub$));
    return this;
  }

  takeUntilDestroyed(destroyRef: DestroyRef) {
    this.destroyRef = destroyRef;
    this.fetchSub$.pipe(takeUntilDestroyed(destroyRef));
    return this;
  }

  subscribe(next: (value: A) => void): Subscription {
    this.load();
    const obs = this.sub$.asObservable();
    if (this.takeUntilSub$) {
      obs.pipe(takeUntil(this.takeUntilSub$));
    } else if (this.destroyRef) {
      obs.pipe(takeUntilDestroyed(this.destroyRef));
    }
    return obs.subscribe((a) => {
      if (a) {
        next(a);
      }
    });
  }

  async latest(): Promise<A> {
    let sub: Subscription;
    const a = await new Promise<A>((resolve) => {
      sub = this.subscribe((suba) => {
        if (suba) {
          resolve(suba);
        }
      });
    });
    sub.unsubscribe();
    return a;
  }

  clear() {
    this.requested = false;
    this.sub$.next(undefined);
  }
}
